/**
 * PhysicsDebug — wireframe visualisation of the BVH, capsule sweeps, contact
 * points and rigid body AABBs. Everything lives under a single group the
 * physics team owns; it is created only when `ctx.config.debug` is set and is
 * invisible until something asks for it (`ctx.physics.setDebug(...)`), so it can
 * never pollute another team's screenshot.
 *
 * owner: physics
 */
import * as THREE from 'three';

const CAP = {
  bvh: 200000,
  bodies: 40000,
  contacts: 40000,
  capsules: 120000,
};

class LineLayer {
  constructor(name, color, capacity, opts = {}) {
    this.capacity = capacity;
    this.array = new Float32Array(capacity * 3);
    this.n = 0;
    const geo = new THREE.BufferGeometry();
    const attr = new THREE.BufferAttribute(this.array, 3);
    attr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('position', attr);
    geo.setDrawRange(0, 0);
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this.attr = attr;
    this.object = new THREE.LineSegments(geo, new THREE.LineBasicMaterial({
      color,
      depthTest: opts.depthTest !== false,
      depthWrite: false,
      transparent: opts.opacity !== undefined,
      opacity: opts.opacity ?? 1,
      toneMapped: false,
    }));
    this.object.name = `physics-debug-${name}`;
    this.object.frustumCulled = false;
    this.object.renderOrder = 9000;
    this.object.userData.noCollide = true;
  }

  begin() { this.n = 0; }
  line(x0, y0, z0, x1, y1, z1) {
    if (this.n + 2 > this.capacity) return;
    const a = this.array, o = this.n * 3;
    a[o] = x0; a[o + 1] = y0; a[o + 2] = z0;
    a[o + 3] = x1; a[o + 4] = y1; a[o + 5] = z1;
    this.n += 2;
  }
  box(mnx, mny, mnz, mxx, mxy, mxz) {
    this.line(mnx, mny, mnz, mxx, mny, mnz); this.line(mxx, mny, mnz, mxx, mny, mxz);
    this.line(mxx, mny, mxz, mnx, mny, mxz); this.line(mnx, mny, mxz, mnx, mny, mnz);
    this.line(mnx, mxy, mnz, mxx, mxy, mnz); this.line(mxx, mxy, mnz, mxx, mxy, mxz);
    this.line(mxx, mxy, mxz, mnx, mxy, mxz); this.line(mnx, mxy, mxz, mnx, mxy, mnz);
    this.line(mnx, mny, mnz, mnx, mxy, mnz); this.line(mxx, mny, mnz, mxx, mxy, mnz);
    this.line(mxx, mny, mxz, mxx, mxy, mxz); this.line(mnx, mny, mxz, mnx, mxy, mxz);
  }
  capsule(cx, cy, cz, halfHeight, radius, segs = 20) {
    const y0 = cy - halfHeight, y1 = cy + halfHeight;
    // three horizontal rings
    for (const y of [y0, cy, y1]) {
      for (let i = 0; i < segs; i++) {
        const a0 = (i / segs) * Math.PI * 2, a1 = ((i + 1) / segs) * Math.PI * 2;
        this.line(
          cx + Math.cos(a0) * radius, y, cz + Math.sin(a0) * radius,
          cx + Math.cos(a1) * radius, y, cz + Math.sin(a1) * radius,
        );
      }
    }
    // two vertical arcs (side profile with hemispherical caps)
    for (const rot of [0, Math.PI / 2]) {
      const dx = Math.cos(rot), dz = Math.sin(rot);
      for (let i = 0; i < segs; i++) {
        const a0 = (i / segs) * Math.PI * 2, a1 = ((i + 1) / segs) * Math.PI * 2;
        const p0 = capPoint(cx, cz, dx, dz, radius, halfHeight, a0, y0, y1);
        const p1 = capPoint(cx, cz, dx, dz, radius, halfHeight, a1, y0, y1);
        this.line(p0[0], p0[1], p0[2], p1[0], p1[1], p1[2]);
      }
    }
  }
  cross(x, y, z, s) {
    this.line(x - s, y, z, x + s, y, z);
    this.line(x, y - s, z, x, y + s, z);
    this.line(x, y, z - s, x, y, z + s);
  }
  end() {
    this.attr.updateRange = { offset: 0, count: this.n * 3 };
    this.attr.needsUpdate = true;
    this.object.geometry.setDrawRange(0, this.n);
  }
  dispose() {
    this.object.geometry.dispose();
    this.object.material.dispose();
  }
}

function capPoint(cx, cz, dx, dz, r, hh, a, y0, y1) {
  const s = Math.sin(a), c = Math.cos(a);
  const yOff = c >= 0 ? y1 : y0;
  return [cx + dx * s * r, yOff + c * r, cz + dz * s * r];
}

export class PhysicsDebug {
  constructor(scene) {
    this.group = new THREE.Group();
    this.group.name = 'physics-debug';
    this.group.visible = false;
    this.group.userData.noCollide = true;
    this.layers = {
      bvh: new LineLayer('bvh', 0x2fd6ff, CAP.bvh, { opacity: 0.42 }),
      bodies: new LineLayer('bodies', 0xffc24a, CAP.bodies, { opacity: 0.8 }),
      contacts: new LineLayer('contacts', 0xff3d5a, CAP.contacts, { depthTest: false }),
      capsules: new LineLayer('capsules', 0x6dff5a, CAP.capsules, { depthTest: false, opacity: 0.9 }),
    };
    for (const k of Object.keys(this.layers)) this.group.add(this.layers[k].object);
    this.flags = { bvh: false, bodies: false, contacts: false, capsules: false };
    this.bvhMaxDepth = 12;
    this.bvhLeavesOnly = false;
    this._staticCapsules = [];
    this._staticLines = [];
    this._bvhDirty = true;   // the BVH wireframe only changes on rebuild
    scene.add(this.group);
  }

  invalidateBVH() { this._bvhDirty = true; }

  setEnabled(flags) {
    if (typeof flags === 'boolean') {
      for (const k of Object.keys(this.flags)) this.flags[k] = flags;
    } else if (flags) {
      Object.assign(this.flags, flags);
    }
    const any = Object.values(this.flags).some(Boolean);
    this.group.visible = any;
    for (const k of Object.keys(this.layers)) this.layers[k].object.visible = !!this.flags[k];
    return any;
  }

  clearMarkers() {
    this._staticCapsules.length = 0;
    this._staticLines.length = 0;
    this._bvhDirty = true;
  }

  /** Persistent capsule marker (used by the step-up shot to draw the walked path). */
  markCapsule(x, y, z, halfHeight, radius) {
    this._staticCapsules.push(x, y, z, halfHeight, radius);
  }
  markLine(x0, y0, z0, x1, y1, z1) {
    this._staticLines.push(x0, y0, z0, x1, y1, z1);
  }

  /** Rebuild the BVH wireframe. Cheap enough to call on rebuild only. */
  update(bvh, bodies, contacts) {
    if (!this.group.visible) return;

    if (this.flags.bvh && this._bvhDirty && bvh && bvh.nodeCount > 0) {
      this._bvhDirty = false;
      const L = this.layers.bvh;
      L.begin();
      const nb = bvh.nodeBounds, nm = bvh.nodeMeta;
      const stack = [0], depth = [0];
      while (stack.length) {
        const node = stack.pop(), d = depth.pop();
        const b = node * 6, m = node * 4;
        const isLeaf = nm[m + 1] > 0;
        if ((!this.bvhLeavesOnly || isLeaf) && d <= this.bvhMaxDepth) {
          L.box(nb[b], nb[b + 1], nb[b + 2], nb[b + 3], nb[b + 4], nb[b + 5]);
        }
        if (!isLeaf && d < this.bvhMaxDepth) {
          stack.push(nm[m]); depth.push(d + 1);
          stack.push(nm[m + 2]); depth.push(d + 1);
        }
      }
      L.end();
    }

    if (this.flags.bodies && bodies) {
      const L = this.layers.bodies;
      L.begin();
      for (const b of bodies) {
        L.box(b.aabbMin.x, b.aabbMin.y, b.aabbMin.z, b.aabbMax.x, b.aabbMax.y, b.aabbMax.z);
      }
      L.end();
    }

    if (this.flags.contacts && contacts) {
      const L = this.layers.contacts;
      L.begin();
      for (let i = 0; i + 5 < contacts.length; i += 6) {
        const x = contacts[i], y = contacts[i + 1], z = contacts[i + 2];
        L.cross(x, y, z, 0.035);
        L.line(x, y, z, x + contacts[i + 3] * 0.22, y + contacts[i + 4] * 0.22, z + contacts[i + 5] * 0.22);
      }
      L.end();
    }

    if (this.flags.capsules) {
      const L = this.layers.capsules;
      L.begin();
      const c = this._staticCapsules;
      for (let i = 0; i + 4 < c.length; i += 5) L.capsule(c[i], c[i + 1], c[i + 2], c[i + 3], c[i + 4], 16);
      const s = this._staticLines;
      for (let i = 0; i + 5 < s.length; i += 6) L.line(s[i], s[i + 1], s[i + 2], s[i + 3], s[i + 4], s[i + 5]);
      L.end();
    }
  }

  dispose() {
    for (const k of Object.keys(this.layers)) this.layers[k].dispose();
    this.group.removeFromParent();
  }
}
